'use client'

import { Download } from '@mui/icons-material'
import { Alert, Box, Button, Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useLazyGetActionArtifactLinkQuery } from '~/data/api/client/assistant'
import { logger } from '~/debug/logger'

type ArtifactDownloadProps = {
  actionId: string
  label: string
}

/** Downloads the file an action produced. The link is presigned and short-lived, so it is fetched on each click. */
export default function ArtifactDownload({ actionId, label }: ArtifactDownloadProps) {
  const t = useTranslations('features.AssistantV2')
  const [fetchArtifactLink, { isFetching }] = useLazyGetActionArtifactLinkQuery()
  const [failed, setFailed] = useState(false)

  const download = async () => {
    setFailed(false)
    try {
      const link = await fetchArtifactLink({ actionId }).unwrap()
      openDownload(link.url)
    } catch (error) {
      logger.error('Failed to fetch an assistant action artifact link', error)
      setFailed(true)
    }
  }

  return (
    <Stack spacing={1}>
      <Box>
        <Button size="small" startIcon={<Download />} disabled={isFetching} onClick={() => void download()}>
          {label}
        </Button>
      </Box>
      {failed && <Alert severity="error">{t('downloadError')}</Alert>}
    </Stack>
  )
}

/** The presigned URL answers with the file as an attachment, so following it downloads without leaving the page. */
function openDownload(url: string) {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.rel = 'noopener'
  // Safari ignores a click on an anchor that is not in the document.
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}
